import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  MenuItem,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';
import OrganisationTable from './components/OrganisationTable';
import Layout from '@src/components/layout/Layout';
import {
  OrganisationSearchResultDTO,
  useSearchOrganisationLazy,
} from '@src/adapters/hooks/organisation/useSearchOrganisation';
import { Link } from 'react-router-dom';
import { Routes } from '@src/navigation/Routes';

type HospitalUsersFilters = {
  isActive: boolean;
  searchQuery: string;
};

const DEFAULT_FILTERS: HospitalUsersFilters = {
  isActive: true,
  searchQuery: '',
};

function Organisations() {
  const { execute: searchOrganisationList } = useSearchOrganisationLazy();
  const [filters, setFilters] = useState<HospitalUsersFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<HospitalUsersFilters>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [organisations, setOrganisations] = useState<
    OrganisationSearchResultDTO[]
  >([]);
  const [recordCount, setRecordCount] = useState(0);

  const findOrganisations = useCallback(async () => {
    const result = await searchOrganisationList({
      pageSize: rowsPerPage,
      currentPage: currentPage,
      searchText: appliedFilters.searchQuery,
      isActive: appliedFilters.isActive,
    });

    setOrganisations(result.records);
    setRecordCount(result.recordCount);
  }, [
    searchOrganisationList,
    rowsPerPage,
    currentPage,
    appliedFilters.searchQuery,
    appliedFilters.isActive,
  ]);

  useEffect(() => {
    findOrganisations();
  }, [findOrganisations]);

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          px: 2,
        }}>
        <Stack
          direction={'row'}
          justifyContent="space-between"
          sx={{ marginBottom: '33px' }}>
          <Typography variant="h4">Organisations</Typography>
          <Button
            component={Link}
            to={Routes.OrganisationAdd}
            variant="outlined"
            size="large"
            color="primary">
            Add New
          </Button>
        </Stack>

        <Stack direction="row" gap={0.75} mb={2}>
          <TextField
            select
            label="Status"
            value={filters.isActive ? 'Active' : 'Inactive'}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                isActive: e.target.value === 'Active',
              }));
            }}
            size="small"
            sx={{
              width: '292px',
            }}>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            placeholder={'Search name'}
            value={filters.searchQuery}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, searchQuery: e.target.value }))
            }
            onKeyDown={handleKeyDown}
            variant="outlined"
            inputProps={{ autoComplete: 'off' }}
            size="small"
            sx={{
              width: '292px',
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Stack>

        <Paper>
          <OrganisationTable
            organisations={organisations}
            selectedPage={currentPage}
            rowsPerPage={rowsPerPage}
            recordCount={recordCount}
            changePage={(value: number) => setCurrentPage(value)}
            changeRowsPerPage={(value: number) => setRowsPerPage(value)}
          />
        </Paper>
      </Box>
    </Layout>
  );
}
export default Organisations;
